import { React, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
// import { useSelector } from "react-redux";
import EditLowStock from "../../core/modals/inventory/editlowstock";
import Table from "../../core/pagination/datatable";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import CommonFooter from "../../core/common/footer/commonFooter";
import CommonDeleteModal from "../../core/common/modal/commonDeleteModal";
import { fetchStockList, deleteLowStockItem } from "../../api/lowStockApi";
import { fetchOutOfStockProducts } from "../../api/outOfStockApi";

import { toast } from "react-toastify";

// ---- S3 image helpers ----
const normalizeKey = (s) => {
  if (!s) return "";
  let v = String(s).trim();
  v = v.replace(/^\/+/, ""); // strip leading slashes
  v = v.replace(/^catelog\//i, "catalog/"); // fix common misspelling
  if (!/^catalog\//i.test(v)) {
    // ensure catalog/ prefix, keep filename
    v = `catalog/${v.split("/").pop()}`;
  }
  return v;
};

const toPublicUrl = (v) => {
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v; // already URL
  const key = normalizeKey(v);
  const base = (
    process.env.REACT_APP_API_IMAGE_BASE_URL ||
    "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
  ).replace(/\/+$/, "");
  return `${base}/${key}`;
};

const LowStock = () => {
  const [dataSource, setDataSource] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [outOfStockData, setOutOfStockData] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState({ id: null, source: null });
  // const dataSource = useSelector((state) => state.rootReducer.lowstock_data);
  const [loadingLowStock, setLoadingLowStock] = useState(true); // 👈 NEW
  const [loadingOutOfStock, setLoadingOutOfStock] = useState(true); // 👈 NEW

  // 🔔 Notify toggle (persist in localStorage)
  const [notifyEnabled, setNotifyEnabled] = useState(() => {
    const v = localStorage.getItem("lowStockNotifyEnabled");
    return v === null ? true : v === "true";
  });
  // 🔔 Keep track of which product ids we already buzzed for
  const notifiedIdsRef = useRef(new Set());
  const audioRef = useRef(null);

  // Setup audio element once
  useEffect(() => {
    audioRef.current = new Audio("/audio/buzzer.mp3");
    audioRef.current.preload = "auto";
    audioRef.current.volume = 1.0; // change if needed
    return () => {
      try {
        audioRef.current && audioRef.current.pause();
      } catch (err) {
        console.error("Error cleaning up audio:", err);
      }
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("lowStockNotifyEnabled", String(notifyEnabled));
    if (!notifyEnabled && audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch (err) {
        console.error("Error cleaning up audio:", err);
      }
    }
  }, [notifyEnabled]);

  // 🔔 Buzz when new low-stock items appear (only if Notify is ON)
  useEffect(() => {
    if (!notifyEnabled || !Array.isArray(dataSource) || dataSource.length === 0)
      return;

    const seen = notifiedIdsRef.current; // Set of ids we've buzzed for
    const incomingIds = dataSource.map((x) => x._id || x.id).filter(Boolean);

    // Find any ids that are new since last render
    const newOnes = incomingIds.filter((id) => !seen.has(id));

    if (newOnes.length > 0 && audioRef.current) {
      // Play once; we don't loop to avoid annoyance
      // The toggle click counts as a user gesture so browsers allow audio.
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {
        // Most browsers will allow after a user click;
        // if blocked, we silently ignore. You could show a toast here.
      });
      // Mark them as notified
      newOnes.forEach((id) => seen.add(id));
    }
  }, [dataSource, notifyEnabled]);

  // low stock data
  useEffect(() => {
    const loadStockData = async () => {
      try {
        setLoadingLowStock(true); // 👈 start loading

        const result = await fetchStockList();

        if (result.success && Array.isArray(result.data)) {
          const formatted = result.data.map((item) => {
            const rawImage = Array.isArray(item.images)
              ? item.images[0]
              : item.images;
            const imageUrl = rawImage
              ? toPublicUrl(rawImage)
              : "/uploads/default.png";
            return {
              _id: item._id || item.id,
              source: item.source,
              warehouse: item.warehouse,
              store: item.store,
              product: item.productName,
              category: item.category,
              sku: item.sku,
              qty: item.quantity,
              qtyalert: item.quantityAlert,
              img: imageUrl,
            };
          });

          setDataSource(formatted);
        } else {
          console.warn("⚠️ Invalid or missing data:", result);
        }
      } catch (err) {
        console.error("Failed to fetch low stock list:", err);
        // optional: toast here
      } finally {
        setLoadingLowStock(false); // 👈 stop loading
      }
    };

    loadStockData();
  }, []);

  // out of stock
  useEffect(() => {
    const t = (s) => (window?.translate ? window.translate(s) : s);

    const loadOutOfStockData = async () => {
      try {
        setLoadingOutOfStock(true); // 👈 start loading

        const result = await fetchOutOfStockProducts();

        const raw = Array.isArray(result)
          ? result
          : Array.isArray(result?.data)
          ? result.data
          : [];

        const formatted = raw.map((item) => {
          const images = Array.isArray(item.images)
            ? item.images.map(toPublicUrl)
            : item.images
            ? [toPublicUrl(item.images)]
            : [];

          return {
            _id: item._id || item.id,
            productName: item.productName ?? "Unnamed",
            sku: item.sku || item.SKU || "",
            images,
            manufacturedDate: item.manufacturedDate || null,
            expiryDate: item.expiryDate || null,
            deletedAt: item.deletedAt || null,
          };
        });

        setOutOfStockData(formatted);
      } catch (error) {
        console.error("Failed to fetch out of stock products", error);
        toast.error(`❌ ${t("Failed to load out-of-stock list")}`);
      } finally {
        setLoadingOutOfStock(false); // 👈 stop loading
      }
    };

    loadOutOfStockData();
  }, []);

  // const handleDelete = async () => {
  //   if (!deleteTarget.id || !deleteTarget.source) {
  //     toast.warning("Invalid delete target.");
  //     return;
  //   }

  //   try {
  //     const result = await deleteLowStockItem(
  //       deleteTarget.id,
  //       deleteTarget.source
  //     );

  //     if (result.success) {
  //       toast.success(result.message);
  //       setDataSource((prev) =>
  //         prev.filter((item) => item._id !== deleteTarget.id)
  //       );
  //       setTimeout(() => {
  //         window.location.reload();
  //       }, 800);
  //     } else {
  //       toast.error(`${window.translate("Something went wrong while deleting")}`);
  //     }
  //   } catch (error) {
  //     console.error("Error deleting item:", error);
  //     toast.error(`${window.translate("Something went wrong while deleting")}`);
  //   } finally {
  //     setDeleteTarget({ id: null, source: null }); // Reset target
  //   }
  // };

  const handleDelete = async () => {
    const t = (s) => (window?.translate ? window.translate(s) : s);

    if (!deleteTarget?.id || !deleteTarget?.source) {
      toast.warning(`⚠️ ${t("Invalid delete target.")}`);
      return;
    }

    try {
      const result = await deleteLowStockItem(
        deleteTarget.id,
        deleteTarget.source
      );

      if (result?.success) {
        toast.success(` ${t("Low stock item deleted successfully")}`);
        setDataSource((prev) =>
          prev.filter((item) => item._id !== deleteTarget.id)
        );

        setTimeout(() => {
          window.location.reload();
        }, 800);
      } else {
        toast.error(`❌ ${t("Something went wrong while deleting")}`);
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error(`❌ ${t("Something went wrong while deleting")}`);
    } finally {
      setDeleteTarget({ id: null, source: null }); // Reset target
    }
  };

  const columns = [
    {
      title: window.translate("Warehouse"),
      dataIndex: "warehouse",

      sorter: (a, b) => a.warehouse.length - b.warehouse.length,
      width: "5%",
    },
    {
      title: window.translate("Store"),
      dataIndex: "store",
      sorter: (a, b) => a.store.length - b.store.length,
    },
    {
      title: window.translate("Product"),
      dataIndex: "product",
      render: (text, record) => (
        <span className="productimgname">
          <Link to="#" className="product-img stock-img">
            <img
              src={toPublicUrl(record.img)}
              alt={text}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "6px",
                objectFit: "cover",
                marginRight: "10px",
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/uploads/default.png";
              }}
            />
          </Link>
          {text}
        </span>
      ),
      sorter: (a, b) => a.product.length - b.product.length,
    },
    {
      title: window.translate("Category"),
      dataIndex: "category",
      sorter: (a, b) => a.category.length - b.category.length,
    },
    {
      title: window.translate("SKU"),
      dataIndex: "sku",
      sorter: (a, b) => a.sku.length - b.sku.length,
    },
    {
      title: window.translate("Qty"),
      dataIndex: "qty",
      sorter: (a, b) => a.qty.length - b.qty.length,
    },
    {
      title: window.translate("Qty Alert"),
      dataIndex: "qtyalert",
      sorter: (a, b) => a.qtyalert.length - b.qtyalert.length,
    },

    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (text, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <Link
              className="me-2 p-2"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#edit-stock"
              onClick={() => {
                console.log("Edit clicked for ID:", record._id);
                setSelectedVariant(record);
              }}
            >
              <i data-feather="edit" className="feather-edit"></i>
            </Link>
            <Link
              data-bs-toggle="modal"
              data-bs-target="#delete-modal"
              className="p-2"
              to="#"
              onClick={() => {
                console.log("DeleteTarget set:", record._id, record.source); // ✅ verify here
                setDeleteTarget({ id: record._id, source: record.source });
              }}
            >
              <i data-feather="trash-2" className="feather-trash-2"></i>
            </Link>
          </div>
        </div>
      ),
    },
  ];

  const outOfStockColumns = [
    {
      title: window.translate("SKU"),
      dataIndex: "sku",
      sorter: (a, b) =>
        (a.sku || "").toString().localeCompare((b.sku || "").toString()),
    },
    {
      title: window.translate("Product"),
      dataIndex: "productName",
      render: (text, record) => {
        const imageSrc = Array.isArray(record.images)
          ? toPublicUrl(record.images[0])
          : toPublicUrl(record.images) || "/uploads/default.png";

        return (
          <span className="productimgname">
            <Link to="#" className="product-img stock-img">
              <img
                src={imageSrc}
                alt="Product"
                style={{
                  width: "30px",
                  height: "30px",
                  objectFit: "cover",
                  borderRadius: "4px",
                }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/uploads/default.png";
                }}
              />
            </Link>
            {text}
          </span>
        );
      },
      sorter: (a, b) =>
        (a.productName || "")
          .toString()
          .localeCompare((b.productName || "").toString()),
      width: "5%",
    },

    {
      title: window.translate("Manufactured Date"),
      dataIndex: "manufacturedDate",
      render: (date) =>
        date
          ? new Date(date).toLocaleDateString(
              localStorage.getItem("language") === "ar" ? "ar-EG" : "en-US"
            )
          : "-",
    },
    {
      title: window.translate("Expiry Date"),
      dataIndex: "expiryDate",
      render: (date) =>
        date
          ? new Date(date).toLocaleDateString(
              localStorage.getItem("language") === "ar" ? "ar-EG" : "en-US"
            )
          : "-",
    },
    {
      title: window.translate("Deleted At"),
      dataIndex: "deletedAt",
      render: (date) =>
        date
          ? new Date(date).toLocaleString(
              localStorage.getItem("language") === "ar" ? "ar-EG" : "en-US"
            )
          : "-",
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="page-title">
              <h4 className="fw-bold">{window.translate("Low Stocks")}</h4>
              <h6>{window.translate("Manage your low stocks")}</h6>
            </div>
            <ul className="table-top-head low-stock-top-head">
              <TooltipIcons />
              <RefreshIcon />
              <CollapesIcon />
              <li>
                <Link
                  to="#"
                  className="btn btn-secondary w-auto shadow-none"
                  data-bs-toggle="modal"
                  data-bs-target="#send-email"
                >
                  <i data-feather="mail" className="feather-mail me-1" />
                  Send Email
                </Link>
              </li>
            </ul>
          </div>
          <div className="table-tab">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
              <ul
                className="nav nav-pills low-stock-tab d-flex me-2 mb-0"
                id="pills-tab"
                role="tablist"
              >
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link active"
                    id="pills-home-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-home"
                    type="button"
                    role="tab"
                    aria-controls="pills-home"
                    aria-selected="true"
                  >
                    {window.translate("Low Stocks")}
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="pills-profile-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-profile"
                    type="button"
                    role="tab"
                    aria-controls="pills-profile"
                    aria-selected="false"
                  >
                    {window.translate("Out of Stocks")}
                  </button>
                </li>
              </ul>
              <div className="notify d-flex bg-white p-1 px-2 border rounded">
                <div className="status-toggle text-secondary d-flex justify-content-between align-items-center">
                  <input
                    type="checkbox"
                    id="low-stock-notify"
                    className="check"
                    checked={notifyEnabled}
                    onChange={(e) => setNotifyEnabled(e.target.checked)}
                  />
                  <label
                    htmlFor="low-stock-notify"
                    className="checktoggle me-2"
                  />
                  {window.translate("Notify")}
                </div>
              </div>
            </div>
            <div className="tab-content" id="pills-tabContent">
              <div
                className="tab-pane fade show active"
                id="pills-home"
                role="tabpanel"
                aria-labelledby="pills-home-tab"
              >
                {/* /product list */}
                <div className="card table-list-card">
                  <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                    <div className="search-set"></div>
                    <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                      <div className="dropdown me-2">
                        <Link
                          to="#"
                          className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                          data-bs-toggle="dropdown"
                        >
                          Warehouse
                        </Link>
                        <ul className="dropdown-menu  dropdown-menu-end p-3">
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Lenovo IdeaPad 3
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Beats Pro{" "}
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Nike Jordan
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Apple Series 5 Watch
                            </Link>
                          </li>
                        </ul>
                      </div>
                      <div className="dropdown me-2">
                        <Link
                          to="#"
                          className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                          data-bs-toggle="dropdown"
                        >
                          Store
                        </Link>
                        <ul className="dropdown-menu  dropdown-menu-end p-3">
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              James Kirwin
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Francis Chang
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Antonio Engle
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Leo Kelly
                            </Link>
                          </li>
                        </ul>
                      </div>
                      <div className="dropdown me-2">
                        <Link
                          to="#"
                          className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                          data-bs-toggle="dropdown"
                        >
                          Category
                        </Link>
                        <ul className="dropdown-menu  dropdown-menu-end p-3">
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Computers
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Electronics
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Shoe
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Electronics
                            </Link>
                          </li>
                        </ul>
                      </div>
                      <div className="dropdown me-2">
                        <Link
                          to="#"
                          className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                          data-bs-toggle="dropdown"
                        >
                          Product
                        </Link>
                        <ul className="dropdown-menu  dropdown-menu-end p-3">
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Lenovo
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Beats
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Nike
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Apple
                            </Link>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      {loadingLowStock ? (
                        // 🔄 Show spinner while loading low stock
                        <div className="d-flex justify-content-center align-items-center py-5">
                          <div className="text-center">
                            <div className="spinner-border" role="status">
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <p className="mb-0 mt-3 text-muted fw-medium">
                              {window.translate(
                                "Loading low stock products..."
                              )}
                            </p>
                          </div>
                        </div>
                      ) : dataSource.length > 0 ? (
                        // ✅ Data loaded, show table
                        <Table
                          columns={columns}
                          dataSource={dataSource}
                          rowKey={(record) => record._id || record.id}
                        />
                      ) : (
                        // 📝 No data, show centered message
                        <div className="d-flex justify-content-center align-items-center py-5">
                          <p className="mb-0 text-muted fw-medium text-center">
                            {window.translate(
                              "Loading products or no data available"
                            )}
                            .
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* /product list */}
              </div>
              <div
                className="tab-pane fade"
                id="pills-profile"
                role="tabpanel"
                aria-labelledby="pills-profile-tab"
              >
                {/* /product list */}
                <div className="card table-list-card">
                  <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                    <div className="search-set"></div>
                    <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                      <div className="dropdown me-2">
                        <Link
                          to="#"
                          className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                          data-bs-toggle="dropdown"
                        >
                          Warehouse
                        </Link>
                        <ul className="dropdown-menu  dropdown-menu-end p-3">
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Lenovo IdeaPad 3
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Beats Pro{" "}
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Nike Jordan
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Apple Series 5 Watch
                            </Link>
                          </li>
                        </ul>
                      </div>
                      <div className="dropdown me-2">
                        <Link
                          to="#"
                          className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                          data-bs-toggle="dropdown"
                        >
                          Store
                        </Link>
                        <ul className="dropdown-menu  dropdown-menu-end p-3">
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              James Kirwin
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Francis Chang
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Antonio Engle
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Leo Kelly
                            </Link>
                          </li>
                        </ul>
                      </div>
                      <div className="dropdown me-2">
                        <Link
                          to="#"
                          className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                          data-bs-toggle="dropdown"
                        >
                          Category
                        </Link>
                        <ul className="dropdown-menu  dropdown-menu-end p-3">
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Computers
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Electronics
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Shoe
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Electronics
                            </Link>
                          </li>
                        </ul>
                      </div>
                      <div className="dropdown me-2">
                        <Link
                          to="#"
                          className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                          data-bs-toggle="dropdown"
                        >
                          Product
                        </Link>
                        <ul className="dropdown-menu  dropdown-menu-end p-3">
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Lenovo
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Beats
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Nike
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Apple
                            </Link>
                          </li>
                        </ul>
                      </div>
                      <div className="dropdown">
                        <Link
                          to="#"
                          className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                          data-bs-toggle="dropdown"
                        >
                          Sort By : Last 7 Days
                        </Link>
                        <ul className="dropdown-menu  dropdown-menu-end p-3">
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Recently Added
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Ascending
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Desending
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Last Month
                            </Link>
                          </li>
                          <li>
                            <Link to="#" className="dropdown-item rounded-1">
                              Last 7 Days
                            </Link>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      {loadingOutOfStock ? (
                        // 🔄 Show spinner while loading
                        <div className="d-flex justify-content-center align-items-center py-5">
                          <div className="text-center">
                            <div className="spinner-border" role="status">
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <p className="mb-0 mt-3 text-muted fw-medium">
                              {window.translate(
                                "Loading out of stock products..."
                              )}
                            </p>
                          </div>
                        </div>
                      ) : outOfStockData.length > 0 ? (
                        // ✅ Data loaded, show table
                        <Table
                          columns={outOfStockColumns} // ⬅ dataIndex is in your columns, unchanged
                          dataSource={outOfStockData}
                          rowKey={(r) => r._id || r.id}
                        />
                      ) : (
                        // 📝 No data, show centered message
                        <div className="d-flex justify-content-center align-items-center py-5">
                          <p className="mb-0 text-muted fw-medium text-center">
                            {window.translate(
                              "No out of stock products available"
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* /product list */}
              </div>
            </div>
          </div>
        </div>
        <CommonFooter />
      </div>

      {/* Send Mail */}
      <div className="modal fade" id="send-email">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="success-email-send modal-body .custom-modal-body text-center">
              <span className="rounded-circle d-inline-flex p-2 bg-success-transparent mb-2">
                <i className="ti ti-checks fs-24 text-success" />
              </span>
              <h4 className="fs-20 fw-semibold">Success</h4>
              <p>Email Sent Successfully</p>
              <Link
                to="#"
                className="btn btn-primary p-1 px-2 fs-13 fw-normal"
                data-bs-dismiss="modal"
              >
                Close
              </Link>
            </div>
          </div>
        </div>
      </div>
      {/* /Send Mail */}

      <EditLowStock selectedVariant={selectedVariant} />
      <CommonDeleteModal onConfirm={handleDelete} />
    </div>
  );
};

export default LowStock;
